import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authHeader, handleUnauthorized } from '../services/auth';
import { MessageCircle, X, Send, Bot } from 'lucide-react';

// Renders **bold**, - bullet lists, 1. numbered lists, --- dividers
function Markdown({ text }) {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      elements.push(<hr key={i} className="border-white/10 my-1" />);
      i++; continue;
    }

    // Table — the assistant returns workouts and meal macros as markdown
    // tables, which used to render as raw pipes. Scrolls sideways rather than
    // squeezing columns to nothing on a phone.
    if (line.trim().startsWith('|') && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1] || '')) {
      const cells = (row) => row.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      const head = cells(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(cells(lines[i]));
        i++;
      }
      elements.push(
        <div key={i} className="my-2 -mx-1 overflow-x-auto">
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr>
                {head.map((h, j) => (
                  <th key={j} className="text-left font-medium px-2 py-1.5 border-b border-white/15 whitespace-nowrap"
                    style={{ color: 'var(--gold)' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, j) => (
                <tr key={j}>
                  {r.map((c, k) => (
                    <td key={k} className="px-2 py-1.5 border-b border-white/8 align-top text-gray-300">
                      {inlineBold(c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Bullet list — collect consecutive bullet lines
    if (/^[-*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s/, ''));
        i++;
      }
      elements.push(
        <ul key={i} className="list-disc list-inside space-y-0.5 my-1">
          {items.map((item, j) => <li key={j}>{inlineBold(item)}</li>)}
        </ul>
      );
      continue;
    }

    // Numbered list — collect consecutive numbered lines
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(
        <ol key={i} className="list-decimal list-inside space-y-0.5 my-1">
          {items.map((item, j) => <li key={j}>{inlineBold(item)}</li>)}
        </ol>
      );
      continue;
    }

    // Empty line → small gap
    if (line.trim() === '') {
      elements.push(<div key={i} className="h-1" />);
      i++; continue;
    }

    // Regular paragraph with inline bold
    elements.push(<p key={i} className="leading-relaxed">{inlineBold(line)}</p>);
    i++;
  }

  return <div className="space-y-0.5 text-sm">{elements}</div>;
}

function inlineBold(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    /^\*\*[^*]+\*\*$/.test(part)
      ? <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>
      : part
  );
}

const CHAT_API = process.env.NODE_ENV === 'production'
  ? '/api/chat'
  : 'http://localhost:3001/chat';

const STARTERS = [
  'How many t-shirts do I have?',
  "What's today's workout?",
  'Show me the best real estate deal',
  "What's my meal plan today?",
];

export default function ChatBubble({ excludePaths = [] }) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [open, setOpen]       = useState(false);
  const [input, setInput]     = useState('');
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hey! I'm your AI assistant. Ask me anything about your wardrobe, workouts, meals, athletes, or real estate — or tell me to make changes." }
  ]);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  async function send(text) {
    const userText = (text || input).trim();
    if (!userText || loading) return;
    setInput('');

    const newMessages = [...messages, { role: 'user', text: userText }];
    setMessages(newMessages);
    setLoading(true);

    // Build API messages array (skip the initial assistant greeting)
    const apiMessages = newMessages
      .filter((_, i) => !(i === 0 && newMessages[0].role === 'assistant'))
      .map(m => ({ role: m.role, content: m.text }));

    try {
      const res = await fetch(CHAT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ messages: apiMessages }),
      });
      if (res.status === 401) {
        handleUnauthorized();
        throw new Error('Session expired — please sign in again.');
      }
      const data = await res.json();
      const reply = data.reply || data.error || 'Something went wrong.';
      setMessages(prev => [...prev, { role: 'assistant', text: reply }]);

      // Refresh module screens when chat edited shared data
      const updated = data.modulesUpdated || [];
      updated.forEach((moduleId) => {
        queryClient.invalidateQueries({ queryKey: ['module', moduleId] });
      });
      if (updated.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['module'] });
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Chat server offline. Make sure chat-server is running on port 3001.' }]);
    } finally {
      setLoading(false);
    }
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  }

  if (excludePaths.includes(location.pathname)) return null;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={!open ? { backgroundColor: 'var(--gold)' } : {}}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-200
          ${open ? 'bg-[#1a1a1a] border border-white/10 scale-95' : 'hover:opacity-90 hover:scale-105'}`}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[360px] h-[520px] bg-[#050505] border border-white/10 rounded-none shadow-2xl flex flex-col overflow-hidden">

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-[#000]">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase font-medium text-white">AI Assistant</p>
              <p className="text-[9px] tracking-[0.2em] uppercase mt-0.5" style={{ color: 'var(--gold)' }}>● Online</p>
            </div>
            <Bot size={14} style={{ color: 'var(--gold)' }} />
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 scrollbar-hide">

            {/* Starter suggestions — show only when only greeting exists */}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {STARTERS.map(s => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-[#C9A84C]/30 hover:border-purple-500/50 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[82%] px-3 py-2 rounded-2xl text-sm leading-relaxed
                    ${msg.role === 'user'
                      ? 'bg-[#C9A84C] text-white rounded-br-sm whitespace-pre-wrap'
                      : 'bg-white/8 text-gray-200 rounded-bl-sm border border-white/5'
                    }`}
                >
                  {msg.role === 'user' ? msg.text : <Markdown text={msg.text} />}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/8 border border-white/5 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1 items-center">
                  {[0, 1, 2].map(i => (
                    <div
                      key={i}
                      className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-3 border-t border-white/10 bg-[#000]">
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Ask anything or give a command..."
                rows={1}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 resize-none outline-none focus:border-[#C9A84C]/50 transition-colors max-h-24"
                style={{ lineHeight: '1.4' }}
              />
              <button
                onClick={() => send()}
                disabled={!input.trim() || loading}
                className="w-9 h-9 rounded-xl bg-[#C9A84C] hover:bg-[#b8962e] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center shrink-0 transition-colors"
              >
                <Send size={15} />
              </button>
            </div>
            <p className="text-[10px] text-gray-600 mt-1.5 text-center">Enter to send · Shift+Enter for new line</p>
          </div>
        </div>
      )}
    </>
  );
}
